/**
 * Vehicle Category Management API Hooks
 * React hooks for managing vehicle category data
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vehicleCategoryApiClient } from './vehicleCategoryApiClient';
import {
  VehicleCategory,
  VehicleCategoryDTO,
  VehicleCategoryListQueryParams,
  ApiResponse,
  PagedData
} from './vehicleCategoryTypes';

/**
 * Hook to fetch a single vehicle category by ID
 * @param id - Vehicle Category ID
 * @param options - Hook options
 */
export function useVehicleCategory(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: VehicleCategory) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await vehicleCategoryApiClient.get<ApiResponse<VehicleCategory>>(`/vehicle-category/${id}`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as VehicleCategory;
  }, [id]);

  return useQuery<VehicleCategory>({
    queryKey: ['vehicle-category', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch paginated list of vehicle categories
 * @param params - Query parameters (page, size, sort, search)
 * @param options - Hook options
 */
export function useVehicleCategories(
  params: VehicleCategoryListQueryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<VehicleCategory>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }

    // For GET request with pagination, we need to send pageable in query params
    const response = await vehicleCategoryApiClient.get<ApiResponse<PagedData<VehicleCategory>>>(
      '/vehicle-category/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as PagedData<VehicleCategory>;
  }, [params.page, params.size, params.sort, params.search, params.isActive]);

  return useQuery<PagedData<VehicleCategory>>({
    queryKey: ['vehicle-categories', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch all vehicle categories (no pagination)
 * @param options - Hook options
 */
export function useAllVehicleCategories(
  options: {
    enabled?: boolean;
    onSuccess?: (data: VehicleCategory[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await vehicleCategoryApiClient.get<ApiResponse<VehicleCategory[]>>(`/vehicle-category/list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as VehicleCategory[];
  }, []);

  return useQuery<VehicleCategory[]>({
    queryKey: ['all-vehicle-categories'],
    queryFn,
    staleTime: 0,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch active vehicle categories
 * @param options - Hook options
 */
export function useActiveVehicleCategories(
  options: {
    enabled?: boolean;
    onSuccess?: (data: VehicleCategory[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await vehicleCategoryApiClient.get<ApiResponse<VehicleCategory[]>>(`/vehicle-category/active-list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as VehicleCategory[];
  }, []);

  return useQuery<VehicleCategory[]>({
    queryKey: ['active-vehicle-categories'],
    queryFn,
    staleTime: 0,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch paginated list of active vehicle categories
 * @param params - Query parameters (page, size, sort, search)
 * @param options - Hook options
 */
export function useActiveVehicleCategoriesPaginated(
  params: VehicleCategoryListQueryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<VehicleCategory>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }

    // For GET request with pagination, we need to send pageable in query params
    const response = await vehicleCategoryApiClient.get<ApiResponse<PagedData<VehicleCategory>>>(
      '/vehicle-category/active-list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as PagedData<VehicleCategory>;
  }, [params.page, params.size, params.sort, params.search, params.isActive]);

  return useQuery<PagedData<VehicleCategory>>({
    queryKey: ['active-vehicle-categories-paginated', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to create a new vehicle category
 * @param options - Hook options
 */
export function useCreateVehicleCategory(
  options: {
    onSuccess?: (data: ApiResponse<VehicleCategory>, variables: VehicleCategoryDTO) => void;
    onError?: (error: Error, variables: VehicleCategoryDTO) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<VehicleCategory>, Error, VehicleCategoryDTO>({
    mutationFn: async (vehicleCategoryData: VehicleCategoryDTO) => {
      const response = await vehicleCategoryApiClient.post<ApiResponse<VehicleCategory>>(
        '/vehicle-category',
        vehicleCategoryData
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all vehicle category queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['all-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories-paginated'] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to update an existing vehicle category
 * @param options - Hook options
 */
export function useUpdateVehicleCategory(
  options: {
    onSuccess?: (
      data: ApiResponse<VehicleCategory>,
      variables: { id: number; data: VehicleCategoryDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: VehicleCategoryDTO }) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<VehicleCategory>, Error, { id: number; data: VehicleCategoryDTO }>({
    mutationFn: async ({ id, data }: { id: number; data: VehicleCategoryDTO }) => {
      const response = await vehicleCategoryApiClient.put<ApiResponse<VehicleCategory>>(
        `/vehicle-category/${id}`,
        data
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate all vehicle category queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['all-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-category', variables.id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to delete a vehicle category
 * @param options - Hook options
 */
export function useDeleteVehicleCategory(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<void>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await vehicleCategoryApiClient.delete<ApiResponse<void>>(`/vehicle-category/${id}`);
      return response;
    },
    onSuccess: (data, id) => {
      // Invalidate all vehicle category queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['all-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-category', id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, id);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to toggle vehicle category active status
 * @param options - Hook options
 */
export function useToggleVehicleCategoryStatus(
  options: {
    onSuccess?: (data: ApiResponse<VehicleCategory>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<VehicleCategory>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await vehicleCategoryApiClient.get<ApiResponse<VehicleCategory>>(
        `/vehicle-category/toggle-active-status/${id}`
      );
      return response;
    },
    onSuccess: (data, id) => {
      // Invalidate all vehicle category queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['all-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories'] });
      queryClient.invalidateQueries({ queryKey: ['active-vehicle-categories-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-category', id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, id);
      }
    },
    onError: options.onError,
  });
}